import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { GetRCsForRecordInput, Query, Rc } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const GET_REASON_CANDIDATES_FOR_RECORD = gql`
  query getRCsForRecord(
    $tenantID: ID!
    $recordID: ID!
    $generatedRCs: [GeneratedRCInput!]!
  ) {
    getRCsForRecord(
      request: {
        tenantID: $tenantID
        recordID: $recordID
        generatedRCs: $generatedRCs
      }
    ) {
      rcs {
        __typename
        ... on UnassignedRC {
          id
          name
          category
          recordIDs
          isSelected
        }
      }
    }
  }
`;

interface GetRCsForFeedbackRecordOptions {
  onCompleted?: (rcs: Rc[]) => void;
  onError?: (error: ApolloError) => void;
  abortSignal?: AbortSignal;
}

export const useGetRCsForFeedbackRecord = ({
  onCompleted,
  onError,
  abortSignal,
}: GetRCsForFeedbackRecordOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [getRCsForRecordQuery, { data, loading }] = useLazyQuery<
    Query,
    GetRCsForRecordInput
  >(GET_REASON_CANDIDATES_FOR_RECORD, {
    fetchPolicy: 'cache-and-network',
    context: {
      fetchOptions: {
        signal: abortSignal,
      },
    },
    onError: (error) => {
      if (error) {
        onError?.(error);
      }
    },
    onCompleted: (data) => {
      if (data.getRCsForRecord?.rcs) {
        onCompleted?.(data.getRCsForRecord?.rcs);
      }
    },
  });

  const getRCsForRecord = useCallback(
    async (input: Omit<GetRCsForRecordInput, 'tenantID'>) => {
      return await getRCsForRecordQuery({
        variables: {
          tenantID,
          ...input,
        },
      });
    },
    [getRCsForRecordQuery, tenantID],
  );

  return [
    getRCsForRecord,
    {
      data: data?.getRCsForRecord?.rcs,
      isLoading: loading,
    },
  ] as const;
};
